import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SectionHeading from '../../ui/SectionHeading';

const PortfolioSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filtered, setFiltered] = useState([]);
  
  const categories = [
    { id: 'all', name: 'All' },
    { id: 'cyberwarfare', name: 'Cyberwarfare' },
    { id: 'hydrocarbon', name: 'Hydrocarbon' },
    { id: 'fintech', name: 'Fintech and Digital Assets' },
    { id: 'ai', name: 'AI' },
    { id: 'metals', name: 'Metals & Minerals' }
  ];
  
  const portfolioItems = [
    // Cyberwarfare companies
    {
      id: 'cyber-venture1',
      name: 'Undisclosed Cyberwarfare Venture',
      category: 'cyberwarfare',
      image: '/images/portfolio/cyber-venture1.jpg',
      description: 'Advanced cyber defense systems for critical infrastructure protection.',
      link: '#'
    },
    {
      id: 'cyber-venture2',
      name: 'Undisclosed Cyberwarfare Venture',
      category: 'cyberwarfare',
      image: '/images/portfolio/cyber-venture2.jpg',
      description: 'Strategic threat intelligence and incident response systems for government and enterprise.',
      link: '#'
    },
    {
      id: 'cyber-venture3',
      name: 'Undisclosed Cyberwarfare Venture',
      category: 'cyberwarfare',
      image: '/images/portfolio/cyber-venture3.jpg',
      description: 'Bleeding-edge technology designed to protect critical digital infrastructure from nation-state threats.',
      link: '#'
    },
    
    // Hydrocarbon companies 
    {
      id: 'caishen-trading',
      name: 'Caishen Trading',
      category: 'hydrocarbon',
      image: '/images/caishen-trading-logo.svg',
      description: 'Specialized trading house for EN590, crude oil, D6 and Jet A1 fuel with global operations.',
      link: '/trading'
    },
    {
      id: 'hyperion',
      name: 'Hyperion Inc',
      category: 'hydrocarbon',
      image: '/images/hyperion-motors-logo.jpg',
      description: 'Leading hydrocarbon company focused on innovative extraction and processing technologies.',
      link: 'https://hyperion.inc'
    },
    
    // Fintech companies
    {
      id: 'sidepocket',
      name: 'Sidepocket',
      category: 'fintech',
      image: '/images/portfolio/sidepocket.jpg',
      description: 'Next-generation financial platform integrating traditional banking with DeFi technologies.',
      link: '#'
    },
    {
      id: 'fintech-venture1',
      name: 'Undisclosed Digital Assets Venture',
      category: 'fintech',
      image: '/images/portfolio/fintech-venture1.jpg',
      description: 'Innovative digital asset management platform with institutional-grade security protocols.',
      link: '#'
    },
    
    // AI companies
    {
      id: 'ai-venture1',
      name: 'Undisclosed AI Venture',
      category: 'ai',
      image: '/images/portfolio/ai-venture1.jpg',
      description: 'Cutting-edge artificial intelligence solutions for enterprise automation and decision support.',
      link: '#'
    },
    {
      id: 'ai-venture2',
      name: 'Undisclosed AI Venture',
      category: 'ai',
      image: '/images/portfolio/ai-venture2.jpg',
      description: 'Advanced machine learning systems focused on predictive analytics and computer vision.',
      link: '#'
    },
    
    // Metals & Minerals companies
    {
      id: 'metals-venture1',
      name: 'Undisclosed Metals & Minerals Venture',
      category: 'metals',
      image: '/images/portfolio/metals-venture1.jpg',
      description: 'Strategic mining operations focused on rare earth elements essential for technology manufacturing.',
      link: '#'
    },
    {
      id: 'metals-venture2',
      name: 'Undisclosed Metals & Minerals Venture',
      category: 'metals',
      image: '/images/portfolio/metals-venture2.jpg',
      description: 'Innovative mineral processing technology that reduces environmental impact while improving yield.',
      link: '#'
    }
  ];
  
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFiltered(portfolioItems);
    } else {
      setFiltered(portfolioItems.filter(item => item.category === selectedCategory));
    }
  }, [selectedCategory]); // portfolioItems is static data, no need in deps
  
  return (
    <section id="portfolio" className="section-padding pt-48 md:pt-56 bg-black relative">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
      
      <div className="container-custom relative z-10">
        <SectionHeading
          subtitle="Our Portfolio"
          title="Investing in the Future"
          description="Our diverse portfolio spans multiple sectors, with a focus on innovative technologies and transformative potential."
        />
        
        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 mt-16">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
        
        {/* Portfolio grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map(item => (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                key={item.id}
                className="group"
              >
                <Link to={item.link} className="block h-full">
                  <div className="glass-card h-full overflow-hidden group-hover:shadow-lg transition-all duration-300">
                    <div className="h-48 overflow-hidden bg-black flex items-center justify-center p-4">
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="object-contain h-full w-full transition-transform duration-500 group-hover:scale-105" 
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 group-hover:text-primary-400 transition-colors">
                        {item.name}
                      </h3>
                      <p className="text-gray-300 text-sm mb-4">
                        {item.description}
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-gray-300">
                          {categories.find(cat => cat.id === item.category)?.name}
                        </span>
                        <span className="text-accent-gold text-sm group-hover:translate-x-1 transition-transform inline-flex items-center">
                          View
                          <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;